import React from 'react';
import { supabase } from '../../lib/supabase';
import { Download, Loader2 } from 'lucide-react';

interface VideoPlayerProps {
  projectId: string;
  storagePath: string;
  onError?: () => void;
  onDurationChange?: (duration: number) => void;
}

export function VideoPlayer({ projectId, storagePath, onError, onDurationChange }: VideoPlayerProps) {
  const [videoUrl, setVideoUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    async function getVideoUrl() {
      try {
        const cleanPath = storagePath.replace(/^interview_videos\//, '');
        const { data, error: signedUrlError } = await supabase.storage
          .from('interview_videos')
          .createSignedUrl(cleanPath, 3600);

        if (signedUrlError) {
          console.error('Error getting signed URL:', signedUrlError);
          throw signedUrlError;
        }

        if (data?.signedUrl) {
          console.log('Successfully got signed URL');
          setVideoUrl(data.signedUrl);
          setError(null);
        } else {
          throw new Error('No signed URL returned');
        }
      } catch (error: any) {
        const errorMessage = error.message || 'Error loading video';
        console.error('Video player error:', errorMessage);
        setError(errorMessage);
        onError?.();
      }
    }

    if (storagePath) {
      getVideoUrl();
    }
  }, [storagePath, onError]);

  const handleLoadedMetadata = () => {
    if (videoRef.current && onDurationChange) {
      onDurationChange(videoRef.current.duration);
    }
  };

  const handleDownload = async () => {
    if (!storagePath) return;
    
    setIsDownloading(true);
    try {
      const cleanPath = storagePath.replace(/^interview_videos\//, '');
      const { data, error } = await supabase.storage
        .from('interview_videos')
        .download(cleanPath);

      if (error) {
        throw error;
      }

      if (data) {
        // Create a download link for the blob
        const url = window.URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = cleanPath.split('/').pop() || 'video.mp4';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading video:', error);
      setError('Failed to download video');
    } finally {
      setIsDownloading(false);
    }
  };

  if (error) {
    return (
      <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-sm text-gray-500 text-center p-4">
          <p>Unable to load video</p>
          <p className="text-xs mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!videoUrl) {
    return (
      <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-sm text-gray-500">Loading video...</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="aspect-video">
        <video
          ref={videoRef}
          className="w-full h-full rounded-lg"
          controls
          src={videoUrl}
          onLoadedMetadata={handleLoadedMetadata}
          onError={(e) => {
            console.error('Video playback error:', e);
            setError('Error playing video');
            onError?.();
          }}
        >
          Your browser does not support the video tag.
        </video>
      </div>
      <button
        onClick={handleDownload}
        disabled={isDownloading}
        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-brand-orange hover:text-brand-orange-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isDownloading ? (
          <>
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            Downloading...
          </>
        ) : (
          <>
            <Download className="h-4 w-4 mr-1" />
            Download Video
          </>
        )}
      </button>
    </div>
  );
}